
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Star, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CreateBoardDialog } from "../board/CreateBoardDialog";

interface Board {
  id: string;
  name: string;
  is_starred: boolean;
  last_viewed_at: string | null;
  background_url: string | null;
  workspace_id: string;
}

interface DashboardProps {
  selectedWorkspace: string;
}

export function Dashboard({ selectedWorkspace }: DashboardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: boards = [], isLoading, error } = useQuery({
    queryKey: ['boards', selectedWorkspace],
    queryFn: async () => {
      try {
        console.log('Fetching boards...');
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('No user found');
          return [];
        }

        // Get all boards from workspaces where user is a member
        const { data: boards, error: boardsError } = await supabase
          .from('boards')
          .select(`
            *,
            workspaces (
              id,
              name,
              organization_id,
              organizations (
                id,
                name,
                color
              )
            )
          `)
          .order('last_viewed_at', { ascending: false });

        if (boardsError) {
          console.error('Error fetching boards:', boardsError);
          throw boardsError;
        }

        // If a workspace is selected, filter boards for that workspace
        const filteredBoards = selectedWorkspace
          ? boards?.filter(board => board.workspace_id === selectedWorkspace)
          : boards;

        console.log('Successfully fetched boards:', filteredBoards?.length || 0);
        return filteredBoards || [];
      } catch (error) {
        console.error('Detailed error in queryFn:', error);
        throw error;
      }
    },
    enabled: true,
  });

  useEffect(() => {
    if (error) {
      console.error('Query error details:', error);
      toast({
        title: t("common.error"),
        description: error instanceof Error ? error.message : t("common.errorOccurred"),
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  const handleBoardClick = async (boardId: string) => {
    try {
      console.log('Attempting to navigate to board:', boardId);
      
      // Update last viewed timestamp
      const { error: updateError } = await supabase
        .from('boards')
        .update({ last_viewed_at: new Date().toISOString() })
        .eq('id', boardId);

      if (updateError) {
        console.error('Error updating last_viewed_at:', updateError);
      }

      // Navigate to the board
      console.log('Navigating to board page...');
      navigate(`/board/${boardId}`);
    } catch (error) {
      console.error("Error handling board click:", error);
      toast({
        title: t("common.error"),
        description: t("common.errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const renderBoardCard = (board: any) => (
    <Card
      key={board.id}
      className="group cursor-pointer hover:shadow-lg transition-all duration-200"
      onClick={() => handleBoardClick(board.id)}
      style={{
        borderColor: board.workspaces?.organizations?.color || 'transparent',
        borderWidth: '2px'
      }}
    >
      <CardHeader className="relative">
        <div 
          className="absolute inset-0 rounded-t-lg bg-cover bg-center"
          style={board.background_url ? {
            backgroundImage: `url(${board.background_url})`,
          } : {
            backgroundColor: 'rgb(var(--card))'
          }}
        >
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors rounded-t-lg" />
        </div>
        <div className="relative z-10 h-24">
          <CardTitle className="text-white">{board.name}</CardTitle>
          {board.is_starred && (
            <Star className="absolute top-2 right-2 h-5 w-5 text-yellow-400" />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {board.last_viewed_at 
              ? `${t("common.lastViewed")}: ${new Date(board.last_viewed_at).toLocaleDateString()}`
              : t("common.neverViewed")}
          </p>
          <p className="text-xs text-muted-foreground">
            {board.workspaces?.organizations?.name} / {board.workspaces?.name}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-8 space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t("common.sidebar.boards")}</h2>
        <CreateBoardDialog onBoardCreated={() => {}} />
      </div>
      {isLoading ? (
        <div className="text-center py-8">{t("common.loading")}</div>
      ) : boards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {boards.map(renderBoardCard)}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">{t("dashboard.noBoards")}</p>
          <CreateBoardDialog onBoardCreated={() => {}} />
        </div>
      )}
    </div>
  );
}
